// ═════════════════════════════════════════════════════════════════════════════
// DxfImporter.js  (v4)
//
// v3 shipped with real gaps against what was actually designed and agreed:
//   - No grain-line confirmation step (silently left to a hardcoded fallback
//     elsewhere in MarkerMaker.js, which always drew grain vertical regardless
//     of the piece's real orientation)
//   - Only the single selected outline shape was imported — every other mark
//     in the piece (grain, notches, internal details) was discarded, despite
//     an explicit earlier instruction that nothing should be ignored
//   - No style/size/cut/way capture at all
//
// v4 fixes all three. Parser logic (BLOCKS parsing, endpoint dedup, closed-loop
// detection, unhandled-entity tracking, units reference-piece detection) is
// unchanged from v3 — already verified against 6 real files — only the UI flow
// and the pattern object v4 builds are new.
// ═════════════════════════════════════════════════════════════════════════════

import React, { useState, useRef, useEffect } from 'react';
import { Upload, ZoomIn, ZoomOut, RotateCcw, Check, X, AlertTriangle } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Parser — unchanged from v3, verified against 6 real files
// ─────────────────────────────────────────────────────────────────────────────

const bboxOf = (pts) => {
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  return { minX, maxX, minY, maxY, w: maxX - minX, h: maxY - minY, area: (maxX - minX) * (maxY - minY) };
};

const KNOWN_SAFE_TO_IGNORE = new Set(['MTEXT', 'POINT', 'DIMENSION', 'ATTDEF', 'ATTRIB', 'LEADER', 'MLEADER']);
// NOTE: 'TEXT' is deliberately NOT in this list here (unlike the standalone
// tool) — v4 needs to read TEXT entities itself to find "Style Name: ..."
// labels, so TEXT is handled explicitly below instead of being skipped.

const readEntitiesRun = (pairs, startIdx, unhandledTracker, textCollector) => {
  const shapes = [];
  let i = startIdx;
  while (i < pairs.length) {
    const { code, value } = pairs[i];
    if (code === '0' && (value === 'ENDSEC' || value === 'ENDBLK')) break;

    if (code === '0' && value === 'LINE') {
      const pts = [[0, 0], [0, 0]];
      i++;
      while (i < pairs.length && pairs[i].code !== '0') {
        const c = pairs[i].code, v = pairs[i].value;
        if (c === '10') pts[0][0] = parseFloat(v);
        else if (c === '20') pts[0][1] = parseFloat(v);
        else if (c === '11') pts[1][0] = parseFloat(v);
        else if (c === '21') pts[1][1] = parseFloat(v);
        i++;
      }
      shapes.push({ type: 'LINE', points: pts });
      continue;
    }

    if (code === '0' && value === 'LWPOLYLINE') {
      const pts = []; let pendingX = null;
      i++;
      while (i < pairs.length && pairs[i].code !== '0') {
        const c = pairs[i].code, v = pairs[i].value;
        if (c === '10') pendingX = parseFloat(v);
        else if (c === '20') { pts.push([pendingX, parseFloat(v)]); pendingX = null; }
        i++;
      }
      if (pts.length >= 2) shapes.push({ type: 'LWPOLYLINE', points: pts });
      continue;
    }

    if (code === '0' && value === 'POLYLINE') {
      const pts = [];
      i++;
      while (i < pairs.length && pairs[i].code !== '0') i++;
      while (i < pairs.length && pairs[i].code === '0' && pairs[i].value === 'VERTEX') {
        i++;
        let vx = null, vy = null;
        while (i < pairs.length && pairs[i].code !== '0') {
          if (pairs[i].code === '10') vx = parseFloat(pairs[i].value);
          else if (pairs[i].code === '20') vy = parseFloat(pairs[i].value);
          i++;
        }
        if (vx !== null && vy !== null) pts.push([vx, vy]);
      }
      if (i < pairs.length && pairs[i].code === '0' && pairs[i].value === 'SEQEND') {
        i++;
        while (i < pairs.length && pairs[i].code !== '0') i++;
      }
      if (pts.length >= 2) shapes.push({ type: 'POLYLINE', points: pts });
      continue;
    }

    if (code === '0' && value === 'TEXT') {
      let textVal = null;
      i++;
      while (i < pairs.length && pairs[i].code !== '0') {
        if (pairs[i].code === '1' && textVal === null) textVal = pairs[i].value;
        i++;
      }
      if (textVal && textCollector) textCollector.push(textVal);
      continue;
    }

    if (code === '0' && value && unhandledTracker && !KNOWN_SAFE_TO_IGNORE.has(value)) {
      unhandledTracker[value] = (unhandledTracker[value] || 0) + 1;
    }
    i++;
    while (i < pairs.length && pairs[i].code !== '0') i++;
  }
  return { shapes, nextIdx: i };
};

export const parseDxfFile = (text) => {
  const rawLines = text.split(/\r\n|\r|\n/).map(l => l.trim());
  const pairs = [];
  for (let i = 0; i + 1 < rawLines.length; i += 2) pairs.push({ code: rawLines[i], value: rawLines[i + 1] });

  const pieces = [];
  const unhandledByPiece = {};
  const unhandledTotal = {};
  const allTextLabels = [];
  let i = 0;

  while (i < pairs.length) {
    const { code, value } = pairs[i];
    if (code === '0' && value === 'SECTION' && pairs[i + 1] && pairs[i + 1].value === 'BLOCKS') {
      i += 2;
      while (i < pairs.length && !(pairs[i].code === '0' && pairs[i].value === 'ENDSEC')) {
        if (pairs[i].code === '0' && pairs[i].value === 'BLOCK') {
          let name = null;
          for (let j = i; j < i + 15 && j < pairs.length; j++) {
            if (pairs[j].code === '2') { name = pairs[j].value; break; }
            if (pairs[j].code === '0' && j > i) break;
          }
          let j = i + 1;
          while (j < pairs.length && pairs[j].code !== '0') j++;
          const pieceUnhandled = {};
          const pieceText = [];
          const { shapes, nextIdx } = readEntitiesRun(pairs, j, pieceUnhandled, pieceText);
          if (shapes.length > 0) {
            const withMeta = shapes.map(s => ({ ...s, bbox: bboxOf(s.points) }));
            pieces.push({ name: name || `Block${pieces.length + 1}`, shapes: withMeta });
          }
          if (Object.keys(pieceUnhandled).length > 0) {
            unhandledByPiece[name || `Block${pieces.length}`] = pieceUnhandled;
            Object.entries(pieceUnhandled).forEach(([t, c]) => { unhandledTotal[t] = (unhandledTotal[t] || 0) + c; });
          }
          allTextLabels.push(...pieceText);
          i = nextIdx;
          if (i < pairs.length && pairs[i].code === '0' && pairs[i].value === 'ENDBLK') i++;
          continue;
        }
        i++;
      }
      continue;
    }
    if (code === '0' && value === 'SECTION' && pairs[i + 1] && pairs[i + 1].value === 'ENTITIES') {
      i += 2;
      const flatText = [];
      const { nextIdx } = readEntitiesRun(pairs, i, {}, flatText);
      allTextLabels.push(...flatText);
      i = nextIdx;
      continue;
    }
    i++;
  }

  const styleNameLabel = allTextLabels.find(t => /style\s*name\s*:/i.test(t));
  const detectedStyleName = styleNameLabel ? styleNameLabel.replace(/style\s*name\s*:\s*/i, '').trim() : null;

  return { pieces, unhandledTotal, unhandledByPiece, detectedStyleName };
};

const dedupe = (shapes, tol = 0.06) => {
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const used = new Array(shapes.length).fill(false);
  const out = [];
  for (let i = 0; i < shapes.length; i++) {
    if (used[i]) continue;
    let best = shapes[i];
    used[i] = true;
    for (let j = i + 1; j < shapes.length; j++) {
      if (used[j]) continue;
      const a = best, b = shapes[j];
      const aS = a.points[0], aE = a.points[a.points.length - 1];
      const bS = b.points[0], bE = b.points[b.points.length - 1];
      const fwd = dist(aS, bS) < tol && dist(aE, bE) < tol;
      const rev = dist(aS, bE) < tol && dist(aE, bS) < tol;
      if (fwd || rev) {
        used[j] = true;
        if (b.points.length > best.points.length) best = b;
      }
    }
    out.push(best);
  }
  return out;
};

const findClosedLoopsAndLeftovers = (shapes, tol = 0.06) => {
  // Returns BOTH the reconstructed closed loops (stitched into complete,
  // ordered point-paths — this is what "outline shape" candidates should
  // actually be) AND the leftover fragments (grain/mark candidates).
  //
  // Earlier versions of this function only tracked WHICH shapes belonged to
  // a closed loop (to correctly exclude them from grain candidates) but
  // never returned the reconstructed loop itself — so the outline picker
  // was showing raw individual segments instead of the real, complete
  // stitched outline. Fixed here.
  const used = new Array(shapes.length).fill(false);
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const loops = [];

  // Self-closed single entities (some files close a boundary within one
  // polyline rather than chaining several open segments together)
  shapes.forEach((s, idx) => {
    const f = s.points[0], l = s.points[s.points.length - 1];
    if (dist(f, l) < tol) { loops.push({ indices: [idx], points: s.points }); used[idx] = true; }
  });

  for (let start = 0; start < shapes.length; start++) {
    if (used[start]) continue;
    let chain = [start]; used[start] = true;
    let currentEnd = shapes[start].points[shapes[start].points.length - 1];
    let orderedPts = [...shapes[start].points];
    let closed = false, guard = 0;
    while (guard++ < shapes.length + 2) {
      let found = -1, reverse = false;
      for (let j = 0; j < shapes.length; j++) {
        if (used[j]) continue;
        const p0 = shapes[j].points[0], p1 = shapes[j].points[shapes[j].points.length - 1];
        if (dist(currentEnd, p0) < tol) { found = j; reverse = false; break; }
        if (dist(currentEnd, p1) < tol) { found = j; reverse = true; break; }
      }
      if (found < 0) break;
      used[found] = true; chain.push(found);
      const pts = reverse ? [...shapes[found].points].reverse() : shapes[found].points;
      orderedPts.push(...pts);
      currentEnd = pts[pts.length - 1];
      if (dist(currentEnd, shapes[start].points[0]) < tol) { closed = true; break; }
    }
    if (closed) loops.push({ indices: chain, points: orderedPts });
  }

  const usedInLoops = new Set(loops.flatMap(l => l.indices));
  const leftovers = shapes.map((s, idx) => ({ s, idx })).filter(x => !usedInLoops.has(x.idx)).map(x => x.s);

  loops.forEach(l => {
    const xs = l.points.map(p => p[0]), ys = l.points.map(p => p[1]);
    l.bbox = bboxOf(l.points);
    l.type = 'RECONSTRUCTED_OUTLINE';
  });

  return { loops, leftovers };
};

const REFERENCE_PIECE_TYPES = [
  { pattern: /cuff/i,         label: 'Cuff',    cmRange: [20, 28] },
  { pattern: /collar/i,       label: 'Collar',  cmRange: [38, 48] },
  { pattern: /pkt|pocket/i,   label: 'Pocket',  cmRange: [12, 18] },
  { pattern: /placket|plkt/i, label: 'Placket', cmRange: [10, 16] },
];

const findReferencePiece = (pieces) => {
  const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  for (const ref of REFERENCE_PIECE_TYPES) {
    const match = pieces.find(p => ref.pattern.test(p.name));
    if (!match) continue;
    const uniqueShapes = dedupe(match.shapes);
    const selfClosed = uniqueShapes.filter(s => {
      const f = s.points[0], l = s.points[s.points.length - 1];
      return dist(f, l) < 0.06;
    });
    let bbox;
    if (selfClosed.length > 0) bbox = selfClosed.sort((a, b) => b.bbox.area - a.bbox.area)[0].bbox;
    else if (uniqueShapes.length > 0) bbox = uniqueShapes.sort((a, b) => b.bbox.area - a.bbox.area)[0].bbox;
    else continue;
    return { pieceName: match.name, label: ref.label, rawW: bbox.w, rawH: bbox.h, cmRange: ref.cmRange };
  }
  return null;
};

// Extract a size token from a piece name — same verified patterns as the
// grain-line tool's extractType(), just returning what was stripped off
// instead of what remained.
const extractSize = (name) => {
  let n = name.trim();
  let m = n.match(/[-_](\d+)[-_](\d+)$/);
  if (m) return m[2];
  m = n.match(/_M-?(\d+)$/);
  if (m) return m[1];
  m = n.match(/[-_](\d+)$/);
  if (m) return m[1];
  return '';
};

// ─────────────────────────────────────────────────────────────────────────────
// Canvas rendering — draws every shape, colour-coded by role
// ─────────────────────────────────────────────────────────────────────────────

const drawShapesOnCanvas = (canvas, allShapes, selectedLoop, grainShape, otherShapes, zoom = 1) => {
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  if (!allShapes || allShapes.length === 0) return;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  allShapes.forEach(s => s.points.forEach(([x, y]) => {
    minX = Math.min(minX, x); minY = Math.min(minY, y);
    maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
  }));
  const padding = 24;
  const w = (maxX - minX) || 10, h = (maxY - minY) || 10;
  const scale = Math.min((canvas.width - 2 * padding) / w, (canvas.height - 2 * padding) / h) * zoom;
  const tx = x => padding + (x - minX) * scale;
  const ty = y => canvas.height - padding - (y - minY) * scale;

  const drawShape = (s, color, width) => {
    ctx.strokeStyle = color; ctx.lineWidth = width;
    ctx.beginPath();
    ctx.moveTo(tx(s.points[0][0]), ty(s.points[0][1]));
    for (let i = 1; i < s.points.length; i++) ctx.lineTo(tx(s.points[i][0]), ty(s.points[i][1]));
    ctx.stroke();
  };

  // Background: every raw shape, faint — just for visual context
  allShapes.forEach(s => {
    if (grainShape && s === grainShape) return;
    if (otherShapes.includes(s)) return;
    drawShape(s, '#DEE2E6', 1);
  });
  otherShapes.forEach(s => drawShape(s, '#a855f7', 1.5)); // internal marks, preserved
  if (grainShape) drawShape(grainShape, '#2E7D32', 2.5);   // confirmed grain
  // Selected outline is a RECONSTRUCTED loop (its own stitched point path,
  // not one of allShapes' raw fragments) — drawn on top as the real boundary
  if (selectedLoop) drawShape(selectedLoop, '#1565C0', 2.5);

  canvas._transform = { tx, ty, minX, minY, scale };
};

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export default function DxfImporter({ onImportPattern, onClose }) {
  const [fileName, setFileName] = useState('');
  const [pieces, setPieces] = useState([]);
  const [unhandled, setUnhandled] = useState(null);
  const [selectedPieceIdx, setSelectedPieceIdx] = useState(null);
  const [selectedShapeIdx, setSelectedShapeIdx] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [parseError, setParseError] = useState('');

  const [showUnitsModal, setShowUnitsModal] = useState(false);
  const [refPiece, setRefPiece] = useState(null);
  const [scaleFactor, setScaleFactor] = useState(1);
  const [scaleFactorLabel, setScaleFactorLabel] = useState('');
  const [manualCm, setManualCm] = useState('');
  const [pendingPieces, setPendingPieces] = useState(null);
  const [pendingUnhandled, setPendingUnhandled] = useState(null);
  const [detectedStyleName, setDetectedStyleName] = useState(null);

  // Grain: null = not yet decided (blocks import), a shape object = confirmed
  // from a candidate, 'manual' = user drew two points, 'skipped' = explicit
  // deliberate skip (still requires an explicit click, never silent default)
  const [grainChoice, setGrainChoice] = useState(null);
  const [grainDrawMode, setGrainDrawMode] = useState(false);
  const [grainDrawPoints, setGrainDrawPoints] = useState([]);

  const [partName, setPartName] = useState('');
  const [styleValue, setStyleValue] = useState('');
  const [sizeValue, setSizeValue] = useState('');
  const [cutValue, setCutValue] = useState('');
  const [wayValue, setWayValue] = useState('');

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const currentPiece = selectedPieceIdx !== null ? pieces[selectedPieceIdx] : null;
  const currentShapes = currentPiece ? dedupe(currentPiece.shapes) : [];
  const { loops: outlineLoops, leftovers: grainCandidates } = currentPiece
    ? findClosedLoopsAndLeftovers(currentShapes)
    : { loops: [], leftovers: [] };
  // Outline candidates are the RECONSTRUCTED, stitched closed loops (a
  // complete panel boundary), sorted largest-first as a hint — never raw
  // individual segments, which would only be a fragment of the real outline.
  const sortedOutlineLoops = [...outlineLoops].sort((a, b) => (b.bbox.w * b.bbox.h) - (a.bbox.w * a.bbox.h));
  // Sort candidates: plain 2-point shapes first (matches verified real-world
  // grain-marker signature) as the suggested-first ordering, never an
  // automatic silent pick.
  const sortedGrainCandidates = [...grainCandidates].sort((a, b) => (a.points.length === 2 ? -1 : 1) - (b.points.length === 2 ? -1 : 1));

  // "Other marks" = every candidate fragment that is neither the outline nor
  // whichever one was chosen as grain — imported and preserved, not discarded.
  const grainShapeObj = grainChoice && grainChoice !== 'manual' && grainChoice !== 'skipped' ? grainChoice : null;
  const otherMarkShapes = grainCandidates.filter(s => s !== grainShapeObj);

  const handleFileUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setParseError('');
    setSelectedPieceIdx(null);
    setSelectedShapeIdx(null);
    setGrainChoice(null);
    setGrainDrawPoints([]);
    setGrainDrawMode(false);

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const { pieces: p, unhandledTotal, unhandledByPiece, detectedStyleName: dsn } = parseDxfFile(ev.target.result);
        if (p.length === 0) {
          setParseError('No pattern pieces found — check the file contains BLOCKS with LINE/LWPOLYLINE/POLYLINE geometry.');
          return;
        }
        setPendingPieces(p);
        setPendingUnhandled({ unhandledTotal, unhandledByPiece });
        setDetectedStyleName(dsn);
        const ref = findReferencePiece(p);
        setRefPiece(ref);
        setShowUnitsModal(true);
      } catch (err) {
        setParseError('Error parsing DXF file: ' + err.message);
      }
    };
    reader.onerror = () => setParseError('File read error.');
    reader.readAsText(f);
    e.target.value = '';
  };

  const confirmUnits = (factor, label) => {
    setScaleFactor(factor);
    setScaleFactorLabel(label);
    setShowUnitsModal(false);
    setPieces(pendingPieces);
    setUnhandled(pendingUnhandled);
  };

  useEffect(() => {
    if (canvasRef.current && currentShapes.length > 0) {
      const selectedLoop = selectedShapeIdx !== null ? sortedOutlineLoops[selectedShapeIdx] : null;
      drawShapesOnCanvas(canvasRef.current, currentShapes, selectedLoop, grainShapeObj, otherMarkShapes, zoom);
    } else if (canvasRef.current) {
      canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  }, [currentShapes, selectedShapeIdx, grainChoice, zoom]);

  useEffect(() => {
    if (currentPiece) {
      setPartName(currentPiece.name);
      setSizeValue(extractSize(currentPiece.name));
      setStyleValue(detectedStyleName || '');
    }
  }, [currentPiece]);

  const handleCanvasClick = (e) => {
    if (!grainDrawMode || !canvasRef.current._transform) return;
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (canvas.height / rect.height);
    const { minX, minY, scale } = canvas._transform;
    const worldX = (mx - 24) / scale + minX;
    const worldY = minY + ((canvas.height - 24 - my) / scale);
    setGrainDrawPoints(prev => {
      const next = prev.length >= 2 ? [] : prev;
      const updated = [...next, [worldX, worldY]];
      if (updated.length === 2) {
        setGrainChoice({ type: 'LINE', points: updated, bbox: bboxOf(updated) });
        setGrainDrawMode(false);
      }
      return updated;
    });
  };

  const canImport = selectedShapeIdx !== null && grainChoice !== null;

  const convertToPattern = () => {
    if (selectedShapeIdx === null) { alert('Click an outline candidate to select it as the pattern outline first.'); return; }
    if (grainChoice === null) { alert('Grain line needs a decision — pick a candidate, draw one manually, or explicitly skip it.'); return; }

    const shape = sortedOutlineLoops[selectedShapeIdx]; // reconstructed, stitched closed loop — not a raw fragment
    const tracedPoints = shape.points.map(([x, y]) => ({ x: +(x * scaleFactor).toFixed(2), y: +(y * scaleFactor).toFixed(2) }));
    if (tracedPoints.length < 3) { alert('Selected outline only has ' + tracedPoints.length + ' point(s) — something is wrong with this loop, pick a different one.'); return; }

    // Grain line is stored as a FRACTION of the outline's own bounding box
    // (fx, fy each 0–1), not absolute cm coordinates — so it stays correctly
    // positioned if the pattern is later reshaped/resized in PatternEditor,
    // instead of staying frozen at its original import-time location.
    const oXs = shape.points.map(p => p[0]), oYs = shape.points.map(p => p[1]);
    const oMinX = Math.min(...oXs), oMaxX = Math.max(...oXs);
    const oMinY = Math.min(...oYs), oMaxY = Math.max(...oYs);
    const oW = (oMaxX - oMinX) || 1, oH = (oMaxY - oMinY) || 1;

    const grainLine = grainShapeObj
      ? grainShapeObj.points.map(([x, y]) => ({
          fx: +(((x - oMinX) / oW)).toFixed(4),
          fy: +(((y - oMinY) / oH)).toFixed(4),
        }))
      : null; // null covers both 'skipped' and never happens for 'manual' (manual already stores as a shape via grainChoice)

    const internalMarks = otherMarkShapes.map(s => ({
      type: s.type,
      points: s.points.map(([x, y]) => ({ x: +(x * scaleFactor).toFixed(2), y: +(y * scaleFactor).toFixed(2) })),
    }));

    const pattern = {
      id: `dxf_${Date.now()}`,
      partName: partName || 'Imported DXF Pattern',
      template: 'custom',
      measurements: {
        width: +(shape.bbox.w * scaleFactor).toFixed(2),
        height: +(shape.bbox.h * scaleFactor).toFixed(2),
      },
      tracedPoints,
      seamAllowances: { enabled: false },
      notches: [],
      grainLine,
      internalMarks,
      style: styleValue || null,
      size: sizeValue || null,
      cut: cutValue || null,
      way: wayValue || null,
      importedFrom: 'DXF',
      sourceFile: fileName,
      sourcePieceName: currentPiece.name,
      unitsScaleFactor: scaleFactor,
      unitsSource: scaleFactorLabel,
      createdOn: new Date().toISOString(),
    };
    onImportPattern(pattern);
  };

  const unhandledTypes = unhandled ? Object.keys(unhandled.unhandledTotal || {}) : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', width: '96vw', maxWidth: '1300px', height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        <div style={{ padding: 16, borderBottom: '1px solid #DEE2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 'bold', color: '#212529' }}>📄 DXF Pattern Importer</h2>
            <div style={{ fontSize: 11, color: '#ADB5BD', marginTop: 2 }}>
              {fileName || 'No file loaded'}{pieces.length > 0 && ` — ${pieces.length} piece(s) — units ×${scaleFactor.toFixed(3)} (${scaleFactorLabel})`}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#6C757D' }}><X size={20} /></button>
        </div>

        {unhandledTypes.length > 0 && (
          <div style={{ margin: '10px 16px 0', padding: '8px 12px', background: '#FFF8E1', border: '1px solid #FF8F00', borderRadius: 6, fontSize: 11, color: '#92400e', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>Entity types not read by this importer: <b>{unhandledTypes.map(t => `${t} (${unhandled.unhandledTotal[t]})`).join(', ')}</b> — outlines for affected pieces may be missing curved sections.</span>
          </div>
        )}

        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {pieces.length > 0 && (
            <div style={{ width: 200, borderRight: '1px solid #DEE2E6', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', fontSize: 11, fontWeight: 'bold', color: '#6C757D', borderBottom: '1px solid #DEE2E6' }}>PIECES ({pieces.length})</div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {pieces.map((p, idx) => (
                  <div key={idx} onClick={() => { setSelectedPieceIdx(idx); setSelectedShapeIdx(null); setGrainChoice(null); setGrainDrawPoints([]); setGrainDrawMode(false); setZoom(1); setCutValue(''); setWayValue(''); }}
                    style={{ padding: '8px 12px', fontSize: 11, cursor: 'pointer', background: selectedPieceIdx === idx ? '#E3F2FD' : 'transparent', color: selectedPieceIdx === idx ? '#1565C0' : '#212529', borderBottom: '1px solid #F4F6F8', fontWeight: selectedPieceIdx === idx ? 'bold' : 'normal' }}>
                    {p.name}<div style={{ fontSize: 9, color: '#ADB5BD', fontWeight: 'normal' }}>{p.shapes.length} shape(s)</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: 16, overflow: 'hidden' }}>
            {!fileName && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                <button onClick={() => fileInputRef.current?.click()} style={{ padding: '14px 24px', background: '#E3F2FD', border: '2px dashed #1565C0', borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, fontWeight: 'bold', color: '#1565C0' }}>
                  <Upload size={18} /> Choose DXF File
                </button>
              </div>
            )}
            {fileName && (
              <>
                <div style={{ display: 'flex', gap: 12, marginBottom: 8, fontSize: 10 }}>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#1565C0', borderRadius: 2, marginRight: 4 }}></span>Outline</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#2E7D32', borderRadius: 2, marginRight: 4 }}></span>Grain</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#a855f7', borderRadius: 2, marginRight: 4 }}></span>Other marks (imported too)</span>
                  <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#DEE2E6', borderRadius: 2, marginRight: 4 }}></span>Undecided</span>
                </div>
                <div style={{ flex: 1, border: '2px solid #DEE2E6', borderRadius: 8, background: '#fafbfc', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <canvas ref={canvasRef} width={640} height={420} onClick={handleCanvasClick} style={{ border: '1px solid #DEE2E6', borderRadius: 4, cursor: grainDrawMode ? 'crosshair' : 'default' }} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                  <button onClick={() => setZoom(z => Math.min(z + 0.2, 4))} style={ctrlBtnStyle}><ZoomIn size={13} /> Zoom In</button>
                  <button onClick={() => setZoom(z => Math.max(z - 0.2, 0.4))} style={ctrlBtnStyle}><ZoomOut size={13} /> Zoom Out</button>
                  <button onClick={() => setZoom(1)} style={ctrlBtnStyle}><RotateCcw size={13} /> Reset</button>
                  <button onClick={() => fileInputRef.current?.click()} style={ctrlBtnStyle}><Upload size={13} /> Change File</button>
                </div>

                {currentPiece && (
                  <div style={{ marginTop: 12, padding: 12, background: '#F8F9FA', borderRadius: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label style={labelStyle}>Part name</label>
                      <input value={partName} onChange={e => setPartName(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Style {detectedStyleName && <span style={{ color: '#2E7D32' }}>(auto-detected, editable)</span>}</label>
                      <input value={styleValue} onChange={e => setStyleValue(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Size {extractSize(currentPiece.name) && <span style={{ color: '#2E7D32' }}>(auto-detected, editable)</span>}</label>
                      <input value={sizeValue} onChange={e => setSizeValue(e.target.value)} style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Cut</label>
                      <select value={cutValue} onChange={e => setCutValue(e.target.value)} style={inputStyle}>
                        <option value="">— select —</option>
                        <option value="1">Cut 1 (single)</option>
                        <option value="2">Cut 2 (pair)</option>
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>Way</label>
                      <select value={wayValue} onChange={e => setWayValue(e.target.value)} style={inputStyle}>
                        <option value="">— select —</option>
                        <option value="1-way">1-way (directional fabric)</option>
                        <option value="2-way">2-way (either direction)</option>
                      </select>
                    </div>
                  </div>
                )}
              </>
            )}
            {parseError && <div style={{ marginTop: 10, padding: '8px 12px', background: '#FFEBEE', border: '1px solid #fecaca', borderRadius: 6, color: '#C62828', fontSize: 11 }}>{parseError}</div>}
            <input ref={fileInputRef} type="file" accept=".dxf,.DXF" onChange={handleFileUpload} style={{ display: 'none' }} />
          </div>

          {currentPiece && (
            <div style={{ width: 280, borderLeft: '1px solid #DEE2E6', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 12px', fontSize: 11, fontWeight: 'bold', color: '#6C757D', borderBottom: '1px solid #DEE2E6' }}>1. OUTLINE SHAPE ({sortedOutlineLoops.length})</div>
              <div style={{ padding: '8px 12px', fontSize: 10, color: '#ADB5BD', borderBottom: '1px solid #F4F6F8' }}>
                {sortedOutlineLoops.length === 0
                  ? 'No closed boundary could be reconstructed for this piece — the segments may not connect cleanly.'
                  : 'Each option is a complete, reconstructed panel boundary (segments stitched together) — usually the largest is the cut line and the next is the seam line.'}
              </div>
              <div style={{ maxHeight: '30%', overflowY: 'auto', borderBottom: '1px solid #DEE2E6' }}>
                {sortedOutlineLoops.map((loop, idx) => (
                  <div key={idx} onClick={() => setSelectedShapeIdx(idx)}
                    style={{ padding: '7px 12px', fontSize: 11, cursor: 'pointer', background: selectedShapeIdx === idx ? '#E3F2FD' : 'transparent', color: selectedShapeIdx === idx ? '#1565C0' : '#212529', borderBottom: '1px solid #F4F6F8', fontWeight: selectedShapeIdx === idx ? 'bold' : 'normal' }}>
                    Outline {idx + 1} ({loop.indices.length} segment{loop.indices.length !== 1 ? 's' : ''} stitched, {loop.points.length}pt){idx === 0 && sortedOutlineLoops.length > 1 ? ' (largest)' : ''}
                    <div style={{ fontSize: 9, color: '#ADB5BD', fontWeight: 'normal' }}>{(loop.bbox.w * scaleFactor).toFixed(1)} × {(loop.bbox.h * scaleFactor).toFixed(1)} cm</div>
                  </div>
                ))}
              </div>

              <div style={{ padding: '10px 12px', fontSize: 11, fontWeight: 'bold', color: '#6C757D', borderBottom: '1px solid #DEE2E6' }}>2. GRAIN LINE — required</div>
              <div style={{ padding: '8px 12px', fontSize: 10, color: '#ADB5BD', borderBottom: '1px solid #F4F6F8' }}>
                {sortedGrainCandidates.length === 0 ? 'No candidates detected — draw one manually or skip.' : 'Pick the real grain line, draw one, or explicitly skip.'}
              </div>
              <div style={{ maxHeight: '30%', overflowY: 'auto', borderBottom: '1px solid #DEE2E6' }}>
                {sortedGrainCandidates.map((s, idx) => (
                  <div key={idx} onClick={() => { setGrainChoice(s); setGrainDrawMode(false); setGrainDrawPoints([]); }}
                    style={{ padding: '7px 12px', fontSize: 11, cursor: 'pointer', background: grainChoice === s ? '#f0fdf4' : 'transparent', color: grainChoice === s ? '#2E7D32' : '#212529', borderBottom: '1px solid #F4F6F8', fontWeight: grainChoice === s ? 'bold' : 'normal' }}>
                    {s.type}, {s.points.length}pt {idx === 0 && sortedGrainCandidates.length > 1 ? '(suggested)' : ''}
                  </div>
                ))}
                <div style={{ padding: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button onClick={() => { setGrainDrawMode(true); setGrainDrawPoints([]); setGrainChoice(null); }}
                    style={{ ...smallBtnStyle, background: grainDrawMode ? '#2E7D32' : '#F8F9FA', color: grainDrawMode ? 'white' : '#212529' }}>
                    {grainDrawMode ? `Click 2 pts (${grainDrawPoints.length}/2)` : '✏️ Draw manually'}
                  </button>
                  <button onClick={() => { setGrainChoice('skipped'); setGrainDrawMode(false); }}
                    style={{ ...smallBtnStyle, background: grainChoice === 'skipped' ? '#FF8F00' : '#F8F9FA', color: grainChoice === 'skipped' ? 'white' : '#212529' }}>
                    Skip (set later)
                  </button>
                </div>
              </div>

              <div style={{ padding: '10px 12px', fontSize: 11, fontWeight: 'bold', color: '#6C757D', borderBottom: '1px solid #DEE2E6' }}>3. OTHER MARKS — auto-imported</div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', fontSize: 10, color: '#6C757D' }}>
                {otherMarkShapes.length === 0 ? 'None remaining.' : `${otherMarkShapes.length} shape(s) will be imported alongside the pattern as internalMarks — nothing is discarded.`}
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: 16, borderTop: '1px solid #DEE2E6', display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
          <div style={{ flex: 1, fontSize: 11, color: canImport ? '#2E7D32' : '#C62828' }}>
            {selectedShapeIdx === null && 'Select an outline shape. '}
            {grainChoice === null && 'Decide on the grain line (pick, draw, or skip). '}
            {canImport && '✓ Ready to import.'}
          </div>
          <button onClick={onClose} style={{ padding: '10px 20px', background: 'white', border: '1px solid #DEE2E6', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, color: '#6C757D' }}>Cancel</button>
          <button onClick={convertToPattern} disabled={!canImport}
            style={{ padding: '10px 20px', background: !canImport ? '#DEE2E6' : '#1565C0', border: 'none', borderRadius: 6, cursor: !canImport ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: 12, color: 'white', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Check size={14} /> Import This Piece
          </button>
        </div>
      </div>

      {showUnitsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'white', borderRadius: 12, padding: 24, maxWidth: 520, width: '90%' }}>
            <h3 style={{ marginTop: 0, fontSize: 16 }}>⚠ Confirm real-world units for this file</h3>
            <p style={{ fontSize: 12, color: '#6C757D', lineHeight: 1.5 }}>This file has no explicit unit declaration. Pick based on which interpretation matches a real garment piece.</p>
            {!refPiece ? (
              <p style={{ fontSize: 12, color: '#C62828' }}>No recognizable reference piece found automatically. Enter the real-world length (cm) of any piece you can identify:</p>
            ) : (
              <>
                <p style={{ fontSize: 12 }}>Reference: <b>{refPiece.pieceName}</b> ({refPiece.label}) — raw size: <b>{refPiece.rawW.toFixed(2)} × {refPiece.rawH.toFixed(2)}</b>. Real {refPiece.label.toLowerCase()}s are typically {refPiece.cmRange[0]}–{refPiece.cmRange[1]}cm.</p>
                {[{ factor: 1, label: 'Raw units ARE centimeters' }, { factor: 2.54, label: 'Raw units are INCHES (× 2.54)' }].map(opt => {
                  const rawMax = Math.max(refPiece.rawW, refPiece.rawH);
                  const asCm = rawMax * opt.factor;
                  const plausible = asCm >= refPiece.cmRange[0] && asCm <= refPiece.cmRange[1];
                  return (
                    <div key={opt.factor} onClick={() => confirmUnits(opt.factor, opt.label)}
                      style={{ border: plausible ? '2px solid #43A047' : '2px solid #DEE2E6', background: plausible ? '#f0fdf4' : 'white', borderRadius: 8, padding: 12, marginBottom: 8, cursor: 'pointer' }}>
                      <b style={{ fontSize: 13 }}>{opt.label}</b>{plausible && <span style={{ marginLeft: 8, fontSize: 9, background: '#43A047', color: 'white', padding: '2px 6px', borderRadius: 4 }}>MATCHES REAL SIZE</span>}
                      <div style={{ fontSize: 11, color: '#6C757D' }}>→ {asCm.toFixed(1)}cm</div>
                    </div>
                  );
                })}
              </>
            )}
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #DEE2E6' }}>
              <span style={{ fontSize: 11, color: '#6C757D' }}>Neither looks right? Enter the real cm length:</span><br />
              <input type="number" value={manualCm} onChange={e => setManualCm(e.target.value)} placeholder="e.g. 24.5" style={{ width: 100, padding: '6px 8px', border: '1px solid #DEE2E6', borderRadius: 6, fontSize: 12, marginTop: 6 }} />
              <button onClick={() => {
                const cmVal = parseFloat(manualCm);
                if (!cmVal || cmVal <= 0) { alert('Enter a valid cm length.'); return; }
                if (!refPiece) { alert('No reference piece — cannot compute scale.'); return; }
                const rawMax = Math.max(refPiece.rawW, refPiece.rawH);
                confirmUnits(cmVal / rawMax, `Manual: ${cmVal}cm`);
              }} style={{ padding: '8px 14px', background: '#343A40', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginLeft: 8 }}>Use this</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ctrlBtnStyle = { padding: '7px 12px', background: '#F8F9FA', border: '1px solid #DEE2E6', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 };
const smallBtnStyle = { padding: '6px 10px', border: '1px solid #DEE2E6', borderRadius: 6, cursor: 'pointer', fontSize: 10, fontWeight: 'bold' };
const labelStyle = { display: 'block', fontSize: 10, fontWeight: 'bold', color: '#6C757D', marginBottom: 3 };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #DEE2E6', borderRadius: 6, fontSize: 11, boxSizing: 'border-box' };
